import { Check, ChevronDown, GitMerge, Phone, ShieldAlert, ShieldCheck, UserCheck, Users, X } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { MigrationUserDetails } from "@/components/migration/migration-user-details";
import {
  formatAdminUserDisplayName,
  formatAdminUserSex,
  isPlatoSynced,
  patientMetaField,
  primaryPhoneDisplay,
} from "@/lib/admin-user-display";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import { initialsFromLabel } from "@/lib/messages/messages-helpers";
import { cn } from "@/lib/utils";

function HeaderChip({ children, tone = "muted" }) {
  const cls =
    tone === "primary"
      ? "border-primary/30 bg-primary/10 text-primary"
      : tone === "emerald"
        ? "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-100"
        : tone === "cyan"
          ? "border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-500/40 dark:bg-cyan-950/60 dark:text-cyan-300"
          : "border-border/60 bg-muted/50 text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        cls,
      )}
    >
      {children}
    </span>
  );
}

/** Avatar + name + role chips + phone inline. Same hero pattern as ThreadMemberDetailsDialog. */
function CandidateHero({ user, phoneOverride }) {
  const displayName = String(formatAdminUserDisplayName(user) || "").trim();
  const heroLabel = displayName || String(user?.id || "").trim() || "User";
  const initials = initialsFromLabel(heroLabel);
  const fallbackColor = getAvatarColorClasses(heroLabel);

  const pm = user?.patientMeta && typeof user.patientMeta === "object" ? user.patientMeta : {};
  const title = String(patientMetaField(pm, "title", "Title") || "").trim();
  const sex = formatAdminUserSex(user);
  const userType = String(user?.userType || "").trim();
  const status = String(user?.status || "").trim();
  const phone = phoneOverride ?? primaryPhoneDisplay(user?.contacts);
  const isActive = status.toLowerCase() === "active";
  const synced = isPlatoSynced(user);

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar className="h-9 w-9 shrink-0 ring-1 ring-border/60">
        <AvatarFallback className={cn("text-xs font-semibold", fallbackColor)}>
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <p className="min-w-0 truncate text-sm font-semibold leading-tight text-foreground" title={heroLabel}>
            {heroLabel}
          </p>
          {phone && phone !== "—" ? (
            <span className="inline-flex shrink-0 items-center gap-1 font-mono text-[11px] tabular-nums text-muted-foreground">
              <Phone className="h-2.5 w-2.5" strokeWidth={2.25} aria-hidden />
              {phone}
            </span>
          ) : null}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {title ? <HeaderChip>{title}</HeaderChip> : null}
          {userType ? <HeaderChip tone="primary">{userType}</HeaderChip> : null}
          {status ? <HeaderChip tone={isActive ? "emerald" : "muted"}>{status}</HeaderChip> : null}
          {sex ? <HeaderChip>{sex}</HeaderChip> : null}
          {synced ? (
            <HeaderChip tone="cyan">
              <ShieldCheck className="mr-1 h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
              Synced
            </HeaderChip>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Selectable candidate card with a visible left accent bar. */
function CandidateCard({ candidate, isSelected, onSelect }) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      className={cn(
        // Left accent bar always renders (border-l-2) so users see a visual anchor — selection
        // just changes the color from muted to primary, instead of appearing/disappearing.
        "relative flex cursor-pointer items-start gap-2.5 overflow-hidden rounded-md border border-l-2 px-3 py-2.5 transition-colors",
        isSelected
          ? "border-l-primary border-primary/60 bg-primary/5 shadow-sm ring-1 ring-primary/30"
          : "border-l-border border-border/70 bg-card hover:border-border hover:bg-muted/40",
      )}
      onClick={() => onSelect(candidate.userId)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(candidate.userId);
        }
      }}
    >
      <span
        className={cn(
          "mt-1.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
          isSelected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-muted-foreground/70 bg-transparent",
        )}
        aria-hidden
      >
        {isSelected ? <Check className="h-2.5 w-2.5" strokeWidth={3.5} /> : null}
      </span>
      <div className="min-w-0 flex-1">
        {candidate.error ? (
          <p className="text-sm text-destructive">
            {candidate.userId}: {candidate.error}
          </p>
        ) : candidate.user ? (
          <Collapsible defaultOpen={false}>
            <CandidateHero user={candidate.user} />
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="group mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground transition hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                <ChevronDown className="h-2.5 w-2.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                <span className="group-data-[state=open]:hidden">Show full record</span>
                <span className="hidden group-data-[state=open]:inline">Hide full record</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 border-t border-border/60 pt-2">
                <MigrationUserDetails user={candidate.user} compact hideHeader />
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <p className="text-sm text-muted-foreground">No data for {candidate.userId}</p>
        )}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onOpenChange
 * @param {object} props.modal
 * @param {(updater: (prev: object) => object) => void} props.setModal
 * @param {() => void} props.onConfirmChoice
 * @param {() => void} props.onConfirmPatientCorrect
 */
export function AmbiguousMigrationDialog({ open, onOpenChange, modal, setModal, onConfirmChoice, onConfirmPatientCorrect }) {
  const candidatesCount = modal.candidates?.length ?? 0;
  const hasSelection = Boolean(modal.selectedUserId);
  const handleSelect = (userId) => setModal((prev) => ({ ...prev, selectedUserId: userId }));
  const memberPhone = modal.threadMemberSnapshot
    ? primaryPhoneDisplay(modal.threadMemberSnapshot.contacts)
    : null;
  // The merge CONSUMES the thread member's record (it is the `fromUserId`), so it may only run on a
  // record Plato does not own — a Plato-synced patient would be recreated by plato_sync's next pass,
  // undoing the merge. "Keep current patient" stays available: it only clears ambiguity metadata.
  const sourceIsPlatoSynced = isPlatoSynced(modal.threadMemberSnapshot);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,820px)] max-w-4xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1.5 border-b border-border/60 bg-muted/30 px-6 pb-4 pt-5 text-left">
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Users className="h-4 w-4" strokeWidth={2.25} />
            </span>
            Choose user for migration
          </DialogTitle>
          <DialogDescription>
            Several user records share the same external identifier. Compare details below and select which account should
            receive the migrated data.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[min(58vh,520px)] min-h-0 w-full px-6">
          <div className="space-y-3 pb-4 pr-3 pt-3">
            {modal.threadMemberSnapshot ? (
              <div className="rounded-md border border-l-2 border-l-amber-500/70 border-border/60 bg-card px-3 py-2.5 shadow-sm">
                <p className="mb-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Phone className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
                  Thread member
                </p>
                <CandidateHero user={modal.threadMemberSnapshot} phoneOverride={memberPhone} />
              </div>
            ) : null}

            <div className="space-y-2">
              <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Users className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
                Matching users
                {candidatesCount > 0 ? (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold leading-none text-muted-foreground">
                    {candidatesCount}
                  </span>
                ) : null}
              </p>
              {modal.loading ? (
                <div className="space-y-1.5">
                  <Skeleton className="h-16 w-full rounded-md" />
                  <Skeleton className="h-16 w-full rounded-md" />
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {modal.candidates.map((c) => (
                    <li key={c.userId}>
                      <CandidateCard
                        candidate={c}
                        isSelected={modal.selectedUserId === c.userId}
                        onSelect={handleSelect}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </ScrollArea>

        {sourceIsPlatoSynced ? (
          <div className="shrink-0 border-t border-amber-500/40 bg-amber-50 px-6 py-3 dark:bg-amber-950/40">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-amber-900 dark:text-amber-100">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
              <span>
                <strong className="font-semibold">This record is synced with Plato.</strong> Plato owns it, so it
                cannot be merged into another patient — the next sync would recreate it. Only records that are not
                Plato-synced can be merged. Use <em>Keep current patient</em> instead.
              </span>
            </p>
          </div>
        ) : null}

        <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={modal.loading || modal.submitting || !modal.threadMemberUserId}
            onClick={onConfirmPatientCorrect}
            className="border-emerald-500/50 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-500/15 dark:hover:text-emerald-200"
          >
            <UserCheck className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            {modal.submitting ? "Saving..." : "Keep current patient"}
          </Button>
          <Button
            type="button"
            disabled={
              modal.loading || modal.submitting || !hasSelection || candidatesCount === 0 || sourceIsPlatoSynced
            }
            title={sourceIsPlatoSynced ? "A Plato-synced patient record cannot be merged." : undefined}
            onClick={onConfirmChoice}
          >
            <GitMerge className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            {modal.submitting ? "Merging..." : "Merge into selected patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
